// Material & labour calculation, client twin of utils/materials/calc.js (live prices and the
// Deckungsbeitrag while editing an offer; the server stays authoritative). Keep everything below
// the header identical to the server file: test/materials/calc.test.js compares both.

const ARTICLE_KINDS = ["article", "labour", "assembly"];
const PRICE_UNITS = [1, 10, 100, 1000];
// 1000 % markup or discount is a typo
const MAX_BP = 100000;
const MAX_CENTS = 100000000;
const MAX_QUANTITY = 1000000;
const MAX_MINUTES = 100000;
const MAX_ASSEMBLY_DEPTH = 3;
const DEFAULT_SETTINGS = {
  wageGroups: [{ code: "GES", name: "Geselle", rate: 0, costRate: 0 }],
  defaultWageGroup: "GES",
  materialMarkup: 2000,
  categoryMarkups: [],
  overhead: 0,
  riskProfit: 0
};

/** Rounds half away from zero; cut to 15 significant digits first (0.35 × 5250 = 1837.5). */
const roundHalfUp = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  return sign * Math.round(Number(Math.abs(n).toPrecision(15)));
};

const clampInt = (value, min, max) => {
  const n = roundHalfUp(value);
  return Math.min(max, Math.max(min, n));
};

/** A quantity with at most 3 decimals, 0 … MAX_QUANTITY. */
const toQuantity = value => {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(MAX_QUANTITY, roundHalfUp(n * 1000) / 1000);
};

/** Minutes per unit with at most 2 decimals. */
const toMinutes = value => {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(MAX_MINUTES, roundHalfUp(n * 100) / 100);
};

const toCode = value =>
  String(value ?? "")
    .trim()
    .slice(0, 20);

/** Company calculation settings with defaults and limits. */
const normalizeSettings = raw => {
  const src = raw || {};
  const seen = new Set();
  const wageGroups = (Array.isArray(src.wageGroups) ? src.wageGroups : DEFAULT_SETTINGS.wageGroups)
    .map(g => ({
      code: toCode(g?.code),
      name: String(g?.name ?? "")
        .trim()
        .slice(0, 80),
      rate: clampInt(g?.rate, 0, MAX_CENTS),
      costRate: clampInt(g?.costRate, 0, MAX_CENTS)
    }))
    .filter(g => g.code && !seen.has(g.code) && seen.add(g.code))
    .slice(0, 30);
  const categories = new Set();
  const categoryMarkups = (Array.isArray(src.categoryMarkups) ? src.categoryMarkups : [])
    .map(c => ({
      category: toCode(c?.category),
      markup: clampInt(c?.markup, -MAX_BP, MAX_BP)
    }))
    .filter(c => c.category && !categories.has(c.category) && categories.add(c.category))
    .slice(0, 200);
  const groups = wageGroups.length ? wageGroups : DEFAULT_SETTINGS.wageGroups;
  const defaultWageGroup = groups.some(g => g.code === src.defaultWageGroup)
    ? src.defaultWageGroup
    : groups[0].code;
  return {
    wageGroups: groups,
    defaultWageGroup,
    materialMarkup: clampInt(
      src.materialMarkup ?? DEFAULT_SETTINGS.materialMarkup,
      -MAX_BP,
      MAX_BP
    ),
    categoryMarkups,
    overhead: clampInt(src.overhead, 0, MAX_BP),
    riskProfit: clampInt(src.riskProfit, 0, MAX_BP)
  };
};

/** Verrechnungssatz (cents per hour) of a wage group: entered, or derived from its wage cost. */
const wageRate = (group, settings) => {
  if (!group) return 0;
  if (group.rate > 0) return group.rate;
  return roundHalfUp(
    (group.costRate * (10000 + settings.overhead) * (10000 + settings.riskProfit)) / 100000000
  );
};

const wageGroupOf = (code, settings) =>
  settings.wageGroups.find(g => g.code === code) ||
  settings.wageGroups.find(g => g.code === settings.defaultWageGroup) ||
  settings.wageGroups[0];

/**
 * Materialzuschlag (bp) for a Warengruppe: the exact group, else the longest configured prefix
 * ("004" covers "004.050"), else the company default.
 */
const markupFor = (category, settings) => {
  const c = String(category || "");
  let best = null;
  for (const entry of settings.categoryMarkups) {
    if (
      c === entry.category ||
      (c.startsWith(entry.category) && c.length > entry.category.length)
    ) {
      if (!best || entry.category.length > best.category.length) best = entry;
    }
  }
  return best ? best.markup : settings.materialMarkup;
};

/**
 * Supplier discount (bp) on the list price: the article's own discount (P record), else its
 * Rabattgruppe at the supplier, else the supplier's general discount.
 */
const discountFor = (article, supplier) => {
  if (Number.isFinite(article?.discount)) return article.discount;
  const group = String(article?.discountGroup || "");
  const entry = group && (supplier?.discountGroups || []).find(g => g.code === group);
  if (entry && Number.isFinite(entry.discount)) return entry.discount;
  return Number(supplier?.defaultDiscount || 0);
};

/**
 * Net purchase price in cents per Preiseinheit: the net price when the supplier sent one, else
 * the list price minus the discount.
 */
const purchasePriceOf = (article, supplier) => {
  const net = Number(article?.purchasePrice || 0);
  if (net > 0) return net;
  const list = Number(article?.listPrice || 0);
  return (list * (10000 - discountFor(article, supplier))) / 10000;
};

/** Material cost per single unit in millicents (unrounded). */
const unitCostMillicents = (article, supplier) => {
  const unit = PRICE_UNITS.includes(Number(article?.priceUnit)) ? Number(article.priceUnit) : 1;
  return (purchasePriceOf(article, supplier) * 1000) / unit;
};

/**
 * The hidden calculation of one unit of an article, labour item or assembly:
 *   { kind, materialCost, materialPrice (millicents), markup (bp), minutes, wageGroup, rate,
 *     costRate (cents/h), fixedPrice (cents or 0) }
 * `ctx` = { settings (normalized), suppliers: Map(id → supplier), articles: Map(id → article) }
 * for assembly components.
 */
const calcArticle = (article, ctx, depth = 0) => {
  const settings = ctx.settings;
  const group = wageGroupOf(article?.labour?.wageGroup, settings);
  const result = {
    kind: ARTICLE_KINDS.includes(article?.kind) ? article.kind : "article",
    materialCost: 0,
    materialPrice: 0,
    markup: 0,
    minutes: toMinutes(article?.labour?.minutes),
    wageGroup: group ? group.code : "",
    rate: wageRate(group, settings),
    costRate: group ? group.costRate : 0,
    fixedPrice: clampInt(article?.fixedPrice, 0, MAX_CENTS)
  };
  if (result.kind === "article") {
    const supplier = ctx.suppliers?.get(String(article.supplier || "")) || null;
    const markup = markupFor(article.category || article.mainCategory, settings);
    result.materialCost = unitCostMillicents(article, supplier);
    result.materialPrice = (result.materialCost * (10000 + markup)) / 10000;
  } else if (result.kind === "assembly" && depth < MAX_ASSEMBLY_DEPTH) {
    // An assembly's own minutes come on top of its components' labour, priced at its wage group
    let labourPrice = (result.minutes * result.rate * 1000) / 60;
    let labourCost = (result.minutes * result.costRate * 1000) / 60;
    for (const component of article.components || []) {
      const part = ctx.articles?.get(String(component.article || ""));
      const quantity = toQuantity(component.quantity);
      if (!part || !quantity) continue;
      const sub = calcArticle(part, ctx, depth + 1);
      if (sub.fixedPrice) {
        result.materialPrice += quantity * sub.fixedPrice * 1000;
        result.materialCost +=
          quantity * (sub.materialCost + (sub.minutes * sub.costRate * 1000) / 60);
        continue;
      }
      result.materialCost += quantity * sub.materialCost;
      result.materialPrice += quantity * sub.materialPrice;
      labourPrice += (quantity * sub.minutes * sub.rate * 1000) / 60;
      labourCost += (quantity * sub.minutes * sub.costRate * 1000) / 60;
      result.minutes += quantity * sub.minutes;
    }
    result.minutes = toMinutes(result.minutes);
    // The assembly's labour as one effective rate, so the line can be re-priced uniformly
    if (result.minutes > 0) {
      result.rate = roundHalfUp((labourPrice * 60) / 1000 / result.minutes);
      result.costRate = roundHalfUp((labourCost * 60) / 1000 / result.minutes);
    }
  }
  result.materialCost = roundHalfUp(result.materialCost);
  result.materialPrice = roundHalfUp(result.materialPrice);
  result.markup =
    result.materialCost > 0
      ? roundHalfUp(((result.materialPrice - result.materialCost) * 10000) / result.materialCost)
      : markupFor(article?.category || article?.mainCategory, settings);
  return result;
};

/** Unit price in cents from a line calculation (what the offer line's price is set to). */
const unitPriceOf = calc => {
  if (!calc) return 0;
  if (calc.fixedPrice > 0) return calc.fixedPrice;
  const labour = (toMinutes(calc.minutes) * Number(calc.rate || 0) * 1000) / 60;
  return Math.max(0, roundHalfUp((Number(calc.materialPrice || 0) + labour) / 1000));
};

/** Unit cost (material + labour) in millicents, unrounded. */
const unitCostOf = calc => {
  if (!calc) return 0;
  return (
    Number(calc.materialCost || 0) +
    (toMinutes(calc.minutes) * Number(calc.costRate || 0) * 1000) / 60
  );
};

/**
 * Changes a line calculation the way the offer editor allows: another Materialzuschlag (bp),
 * minutes or wage group. Returns a new calculation; materialPrice follows the markup.
 */
const adjustCalc = (calc, changes, settings) => {
  const next = { ...calc };
  if (changes.markup !== undefined) {
    next.markup = clampInt(changes.markup, -MAX_BP, MAX_BP);
    next.materialPrice = roundHalfUp(
      (Number(next.materialCost || 0) * (10000 + next.markup)) / 10000
    );
  }
  if (changes.minutes !== undefined) next.minutes = toMinutes(changes.minutes);
  if (changes.wageGroup !== undefined && settings) {
    const group = wageGroupOf(changes.wageGroup, settings);
    next.wageGroup = group ? group.code : "";
    next.rate = wageRate(group, settings);
    next.costRate = group ? group.costRate : 0;
  }
  if (changes.fixedPrice !== undefined)
    next.fixedPrice = clampInt(changes.fixedPrice, 0, MAX_CENTS);
  return next;
};

/** Net of a line in cents: quantity × unit price, rounded half up. */
const lineNet = (quantity, unitPriceCents) =>
  roundHalfUp(toQuantity(quantity) * Number(unitPriceCents || 0));

/** Unit price in cents of an offer item (legacy euro float `price`). */
const itemUnitPrice = item => {
  const n = Number(String(item?.price ?? "").replace(",", "."));
  return Number.isFinite(n) ? roundHalfUp(n * 100) : 0;
};

/**
 * Deckungsbeitrag of offer items: revenue, material cost, labour cost (cents), labour minutes,
 * contribution, margin (bp of revenue), and the revenue of lines without a calculation (their
 * cost is unknown, so the contribution is overstated by up to that amount).
 */
const summarizeMargin = items => {
  const totals = {
    revenue: 0,
    materialCost: 0,
    labourCost: 0,
    labourMinutes: 0,
    contribution: 0,
    margin: 0,
    uncalculatedRevenue: 0,
    lines: 0,
    calculatedLines: 0
  };
  for (const item of items || []) {
    const quantity = toQuantity(item?.quantity);
    const net = lineNet(quantity, itemUnitPrice(item));
    totals.revenue += net;
    totals.lines++;
    const calc = item?.calc;
    if (!calc || typeof calc !== "object") {
      totals.uncalculatedRevenue += net;
      continue;
    }
    totals.calculatedLines++;
    totals.materialCost += roundHalfUp((quantity * Number(calc.materialCost || 0)) / 1000);
    totals.labourCost += roundHalfUp(
      (quantity * toMinutes(calc.minutes) * Number(calc.costRate || 0)) / 60
    );
    totals.labourMinutes += quantity * toMinutes(calc.minutes);
  }
  totals.labourMinutes = roundHalfUp(totals.labourMinutes);
  totals.contribution = totals.revenue - totals.materialCost - totals.labourCost;
  totals.margin = totals.revenue ? roundHalfUp((totals.contribution * 10000) / totals.revenue) : 0;
  return totals;
};

/**
 * The offer item for `quantity` units of an article: the visible fields the offer and invoices
 * use (price in euros like every legacy offer item) plus the hidden calculation.
 */
const offerItemFor = (article, calc, quantity) => {
  const unitPrice = unitPriceOf(calc);
  const q = toQuantity(quantity) || 1;
  return {
    description: [article.name, article.shortText2].filter(Boolean).join(" ").slice(0, 300),
    longText: String(article.longText || "").slice(0, 8000),
    quantity: q,
    unit: article.unit || "Stk",
    price: unitPrice / 100,
    total: lineNet(q, unitPrice) / 100,
    articleId: article._id || null,
    articleNumber: article.articleNumber || "",
    costCents: roundHalfUp(unitCostOf(calc) / 1000),
    laborMinutes: toMinutes(calc.minutes),
    calc
  };
};

export {
  ARTICLE_KINDS,
  PRICE_UNITS,
  MAX_BP,
  MAX_CENTS,
  MAX_ASSEMBLY_DEPTH,
  DEFAULT_SETTINGS,
  roundHalfUp,
  toQuantity,
  toMinutes,
  normalizeSettings,
  wageRate,
  wageGroupOf,
  markupFor,
  discountFor,
  purchasePriceOf,
  unitCostMillicents,
  calcArticle,
  unitPriceOf,
  unitCostOf,
  adjustCalc,
  lineNet,
  itemUnitPrice,
  summarizeMargin,
  offerItemFor
};
